import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import {
    Box,
    Card,
    Chip,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import type { EntitySpec } from '../../models/Models';
import { displayName, iconButtonSx } from './helpers';
import { useRelationCounts } from './hooks';
import ProfileTab from './Profile';
import RelationTab from './RelationTab';
import type { AnyEntity } from './types';

interface DetailProps {
  spec: EntitySpec;
  item: AnyEntity;
  tab: number;
  onTabChange: (tab: number) => void;
  subtab: number | null;
  onSubtabChange: (id: number | null) => void;
  onEdit: (group: string) => void;
  onDelete: () => void;
}

const Detail = ({ spec, item, tab, onTabChange, subtab, onSubtabChange, onEdit, onDelete }: DetailProps) => {
  const { can } = useAuth();

  const counts = useRelationCounts(spec.relations, item);
  const relations = spec.relations ?? [];
  // Clamp the active tab so it can never exceed the number of available tabs.
  const safeTab = Math.min(tab, relations.length);

  const name = displayName(item, spec.singular);
  const headquarters = (item as Record<string, unknown>).headquarters as string | undefined;
  const country = (item as Record<string, unknown>).country as string | undefined;
  const logo = (item as Record<string, unknown>).logo as string | undefined;
  const hasLogo = typeof logo === 'string' && logo.startsWith('data:image');
  const subtitleParts = [headquarters, country].filter(Boolean);

  return (
    <Box>
      {/* Hero */}
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F9FD 100%)',
        }}
      >
        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {hasLogo && (
            <Box
              component="img"
              src={logo}
              alt={`${name} logo`}
              sx={{
                width: 56,
                height: 56,
                objectFit: 'contain',
                borderRadius: 1,
                border: '1px solid #EEF1F5',
                bgcolor: '#FFFFFF',
                p: 0.5,
                flexShrink: 0,
              }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, color: '#002A5C' }}>
              {name}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
              <Chip
                size="small"
                label={spec.singular}
                sx={{
                  bgcolor: 'rgba(0,157,224,0.1)',
                  color: '#0087C3',
                  fontWeight: 600,
                  height: 22,
                }}
              />
              {subtitleParts.length > 0 && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#B0BAC5' }} />
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <PublicOutlinedIcon sx={{ fontSize: 14, color: '#8E99A8' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {subtitleParts.join(' · ')}
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            {can('update') && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={onDelete} sx={iconButtonSx('delete')}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Tabs
          value={safeTab}
          onChange={(_, v) => onTabChange(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#FFFFFF',
            minHeight: 44,
          }}
        >
          <Tab label="Profile" />
          {relations.map((r) => (
            <Tab key={r.key} label={`${r.label} (${counts[r.key] ?? 0})`} />
          ))}
        </Tabs>
      </Card>

      {safeTab === 0 && <ProfileTab spec={spec} item={item} onEdit={can('update') ? onEdit : undefined} />}
      {safeTab > 0 && relations[safeTab - 1] && (
        <RelationTab
          rel={relations[safeTab - 1]}
          item={item}
          subtab={subtab}
          onSubtabChange={onSubtabChange}
        />
      )}
    </Box>
  );
};

export default Detail;
